import _ from "lodash";
import { VehicleConditionalCardData } from "../types/blocks";
import { PropertyData, PropertyStyles, PropertyVisualStyle, VehicleProperty } from "../types/site";
import { Vehicle } from "../types/vehicle";
import { formatNumber } from "./stringFormatter";

export function getVehiclePropertyValue(vehicle: any, property: VehicleProperty): string {
  if (property.data === PropertyData.PROPERTY_VALUE || property.data === PropertyData.SEPARATOR) {
    if (property.name === "vin") {
      if (vehicle.vin !== undefined && !isFakeVin(vehicle.vin)) {
        return vehicle.vin;
      }
      return "";
    }
    return vehicle[property.name] ? vehicle[property.name].toString() : "";
  }

  if (property.data === PropertyData.SLASH) {
    if (vehicle[property.internalProperties![0]] && vehicle[property.internalProperties![1]]) {
      return `${vehicle[property.internalProperties![0]].toString()} / ${vehicle[
        property.internalProperties![1]
      ].toString()}`;
    }
  }
  if (
    property.data === PropertyData.DIFFERENCE &&
    property.internalProperties &&
    vehicle[property.internalProperties[0]] !== undefined
  ) {
    let result = vehicle[property.internalProperties[0]];
    if (property.internalProperties.length === 1) {
      return (-result).toString();
    }
    for (let i = 1; i < property.internalProperties.length; i++) {
      if (!vehicle[property.internalProperties[i]]) {
        break;
      }
      result -= vehicle[property.internalProperties[i]];
    }
    return result.toString();
  }

  return "";
}

const PRICE_PROPERTIES = _.without(_.values(PropertyVisualStyle), PropertyVisualStyle.PLAIN_TEXT);

export function isPriceProperty(propertyVisualStyle: PropertyVisualStyle): boolean {
  return PRICE_PROPERTIES.includes(propertyVisualStyle);
}

export function isNumberProperty(propertyName: string): boolean {
  return propertyName === "mileage";
}

export function vehiclePropertyFormatter(vehicle: Vehicle, property: VehicleProperty): string | undefined {
  let propertyValue = getVehiclePropertyValue(vehicle, property);

  if (!propertyValue) {
    return undefined;
  }

  if (isNumberProperty(property.name) || isPriceProperty(property.visualStyle)) {
    if (propertyValue === "0" && isPriceProperty(property.visualStyle)) {
      return "";
    }
    const formatValue = formatNumber(propertyValue);
    return formatValue;
  }

  return propertyValue;
}

export function vehiclePriceFormatter(vehicle: Vehicle, priceField?: string): string | undefined {
  const priceValue: number = (vehicle as any)[priceField || "price"];
  return priceValue ? formatNumber(priceValue) : "";
}

export const OBLIGATORY_FIELDS = [
  "photosMedium",
  "make",
  "model",
  "trim",
  "type",
  "year",
  "dealer",
  "vin",
  "stock",
  "transitStatus",
];

export function isFakeVin(vin: string): boolean {
  return vin.startsWith("FAKE:");
}

function fillVehiclePropertiesFromvehicleProperty(vehicleFieldsSet: Set<string>, property: VehicleProperty) {
  vehicleFieldsSet.add(property.name);
  if (property.name === "extColor") {
    vehicleFieldsSet.add("color");
  }
  if (property.name === "intColor") {
    vehicleFieldsSet.add("colorInternal");
  }
  property.internalProperties?.forEach((internalProperty) => {
    vehicleFieldsSet.add(internalProperty);
  });
}

export function fillVehiclePropertiesFromConditionalCard(
  vehicleFieldsSet: Set<string>,
  card: VehicleConditionalCardData,
) {
  card.infoTab.properties?.forEach((property) => {
    fillVehiclePropertiesFromvehicleProperty(vehicleFieldsSet, property);
  });
  card.priceTab.properties?.forEach((property) => {
    fillVehiclePropertiesFromvehicleProperty(vehicleFieldsSet, property);
  });
  if (card.priceField) {
    vehicleFieldsSet.add(card.priceField);
  }
}

export function visualPriceStyleFromPropertyStyle(propertyStyle?: PropertyStyles): PropertyVisualStyle {
  switch (propertyStyle) {
    case PropertyStyles.INVERTED:
      return PropertyVisualStyle.INVERTED;
    case PropertyStyles.STRIKEOUT_TEXT:
      return PropertyVisualStyle.STRIKEOUT_PRICE;
    case PropertyStyles.TEXT:
    case PropertyStyles.TEXT_WITH_DISCLAIMER:
      return PropertyVisualStyle.PLAIN_PRICE;
  }

  return PropertyVisualStyle.PLAIN_PRICE;
}
